import type { PaginatedQuery } from "@shared/types/list";
import { DEFAULT_PAGE_SIZE } from "@shared/types/list";
import type { CombinedSearchValue } from "@shared/utils/filterQuery";
import { resolveCombinedSearchForRequest, resolveMultiSelectForRequest } from "@shared/utils/filterQuery";
import { PRODUCT_PAIRING_INVENTORY_TYPE_BY_VIEW, type ProductPairingViewMode } from "./data";

export interface ProductPairingQueryParam extends PaginatedQuery {
    viewMode: ProductPairingViewMode;
    /** UiMultipleSelect */
    corpId: string[];
    /** UiMultipleSelect：1P仓库，对应请求 externalWarehouseId；空=全部 */
    externalWarehouseId: string[];
    /** UiMultipleSelect，对应请求 matchStatus */
    isMapping: (string | number)[];
    /** UiCombinedSelect：模糊 string / 精确 string[] → 产品名称（海外仓产品名） */
    productNameSnapshot: CombinedSearchValue;
    /** UiCombinedSelect：模糊 string / 精确 string[] → 品名（配对产品） */
    productName: CombinedSearchValue;
    /** UiCombinedSelect：模糊 string / 精确 string[] */
    warehouseSku: CombinedSearchValue;
    sku1p: CombinedSearchValue;
    asin: CombinedSearchValue;
    msku: CombinedSearchValue;
}

export const DEFAULT_PRODUCT_PAIRING_QUERY: ProductPairingQueryParam = {
    viewMode: "product",
    corpId: [],
    externalWarehouseId: [],
    isMapping: [],
    productNameSnapshot: "",
    productName: "",
    warehouseSku: "",
    sku1p: "",
    asin: "",
    msku: "",
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
};

/** POST /manage/warehouseProductMatch/list 请求体 */
export function toProductPairingListRequestParams(query: ProductPairingQueryParam): Record<string, unknown> {
    const body: Record<string, unknown> = {
        page: query.page,
        perPage: query.limit,
        inventoryType: PRODUCT_PAIRING_INVENTORY_TYPE_BY_VIEW[query.viewMode],
    };

    const corpId = resolveMultiSelectForRequest(query.corpId);
    if (corpId !== undefined) body.corpId = corpId;

    const externalWarehouseId = resolveMultiSelectForRequest(query.externalWarehouseId);
    if (externalWarehouseId !== undefined) body.externalWarehouseId = externalWarehouseId;

    const matchStatus = resolveMultiSelectForRequest(query.isMapping);
    if (matchStatus !== undefined) body.matchStatus = matchStatus.map(Number);

    const productNameSnapshot = resolveCombinedSearchForRequest(query.productNameSnapshot);
    if (productNameSnapshot !== undefined) body.productNameSnapshot = productNameSnapshot;

    const productName = resolveCombinedSearchForRequest(query.productName);
    if (productName !== undefined) body.productName = productName;

    const warehouseSku = resolveCombinedSearchForRequest(query.warehouseSku);
    if (warehouseSku !== undefined) body.warehouseSku = warehouseSku;

    const sku1p = resolveCombinedSearchForRequest(query.sku1p);
    if (sku1p !== undefined) body.sku1p = sku1p;

    const asin = resolveCombinedSearchForRequest(query.asin);
    if (asin !== undefined) body.asin = asin;

    const msku = resolveCombinedSearchForRequest(query.msku);
    if (msku !== undefined) body.msku = msku;

    return body;
}

/** POST /manage/warehouseProductMatch/export 请求体 */
export function toProductPairingExportRequestParams(query: ProductPairingQueryParam): Record<string, unknown> {
    const params = toProductPairingListRequestParams(query);
    delete params.page;
    delete params.perPage;

    return params;
}
